import type { Role } from '@/features/auth/api';

/**
 * 操作マニュアルの本文（画面ごとに独立したページ / topics.ts の HELP_TOPICS と1:1）。
 *
 * 読み手はパソコンに慣れていない事務局・受講生を想定する。
 * 手順は「どこをクリックするか」から書き、途中の確認画面も省略しない。
 * ボタン名・タブ名は実際の画面表記に合わせる。内部コードは書かない。
 *
 * 手順・注意点は `roles` で対象を絞り、今のロールに関係するものだけを表示する。
 * `roles` を省略した項目は、そのトピックを開ける全ロールに表示する。
 */
export interface HelpProcedure {
  /** 操作の目的（「〜する」）。ロール名は入れない */
  title: string;
  roles?: Role[];
  steps: string[];
}

export interface HelpNote {
  text: string;
  roles?: Role[];
}

export interface HelpContent {
  summary: Partial<Record<Role, string>> & { fallback: string };
  procedures: HelpProcedure[];
  notes?: HelpNote[];
}

const OFFICE: Role[] = ['office'];
const STUDENT: Role[] = ['student'];
const DIRECTOR: Role[] = ['director'];
const SUCCESS: Role[] = ['success'];
const SUPER_ADMIN: Role[] = ['super_admin'];
const OFFICE_AND_SUPER: Role[] = ['office', 'super_admin'];

export const HELP_CONTENT: Record<string, HelpContent> = {
  dashboard: {
    summary: {
      fallback:
        'ログインした直後に出るホーム画面です。いま自分に関係することだけが並びます。青い文字をクリックすると、その作業の画面が開きます。',
      student:
        'ログインした直後のホームです。自分が参加する近いイベント、まだ出していない課題やアンケート、選べる面談の日時、新しいメッセージがまとまっています。',
      director:
        'ログインした直後のホームです。自分が担当する近いイベント、関係する会員の人数、新しいメッセージがまとまっています。',
      success:
        'ログインした直後のホームです。近いイベント、「今週の面談（確定済み）」、担当会員の人数、新しいメッセージがまとまっています。',
      office:
        'ログインした直後のホームです。上のほうに「いま対応が必要なこと」（欠席の承認待ち、問い合わせの未読、受講生がまだ出していない課題やアンケートなど）が並びます。その下に直近のイベントがあります。',
      super_admin:
        'システムを預かる会社向けの入口です。受講生の名簿やイベントの中身は、ここには出ません。',
    },
    procedures: [
      {
        title: '画面の見かた（共通）',
        steps: [
          'パソコンの画面の左に、縦のメニューがあります。やりたいことの名前をクリックします。メニューが狭いときは、上の「三」のボタンで開きます',
          'いま見ている画面の名前は、白い部分のいちばん上に大きく出ます。その下の「❓使い方を見る」で、その画面の説明が開きます',
          '青い文字はリンクです。クリックすると別の画面に移ります',
          '赤い丸の数字は「まだ読んでいないお知らせ」です。作業の残り件数ではありません',
          '赤い文字のボタン（取り消す・削除するなど）を押すと、確認の画面が必ず出ます。確認するまで実行されません',
        ],
      },
      {
        title: 'ホームから作業の画面へ進む',
        steps: [
          'ホームに出ているイベント名や「○件」などの青い文字をクリックする',
          'イベントの詳細、メッセージ、会員の画面など、その作業の場所が開く',
          '作業が終わったら、左メニューの「ダッシュボード」でホームに戻る',
        ],
      },
      {
        title: '事務局：対応が必要なものを処理する',
        roles: OFFICE,
        steps: [
          'ホームの先頭付近にある対応リストを上から読む',
          '欠席の承認待ちなら、リンクからそのイベントの「出欠」タブが開くので、承認するか差し戻すかを選ぶ',
          '受講生の未回答なら、リンクからそのイベントの該当タブ（出欠や課題・アンケート）が開く',
          '「問い合わせの未読」は、まだ読んでいない会話の件数です。「問い合わせ対応」が開くので、会話を読んで返信する',
          '「出欠の未回答」は、出欠を取る回だけを数えます。月例会は出欠を取らないので、ここには出ません',
        ],
      },
    ],
    notes: [
      {
        text: '初めて使うときは、左メニューのいちばん下「操作マニュアル」を開いてください。この説明の一覧があります。',
      },
      {
        text: 'ログインできないときは、受講生・理事・講師・サクセス担当は画面の「パスワードをお忘れの方」を使います。事務局は認証アプリ付きの専用のログイン画面です。どうしても入れないときは、別の事務局の管理責任者か、システム運営会社へ連絡してください。',
      },
    ],
  },

  members: {
    summary: {
      fallback: '受講生（会員）の名簿と、1人ずつの詳しい情報を見る画面です。',
      student:
        'ほかの受講生の公開プロフィール（顔写真・会社名・自己紹介など）を見る画面です。電話番号などの非公開の連絡先は出ません。自分の情報を直すのは左メニュー「マイプロフィール」です。',
      director:
        '自分が担当した回の対象になった受講生だけが一覧に出ます（左メニュー「関わった会員」）。「詳細」で見えるのは公開プロフィールだけです。提出物・出欠・アンケートの回答を見るときは、一覧の行の「年間ポートフォリオ」を開きます。電話番号などの非公開の連絡先は出ません。',
      success:
        '自分が担当した回の対象になった受講生だけが一覧に出ます（左メニュー「関わった会員」）。「詳細」で見えるのは公開プロフィールだけです。提出物・出欠・アンケートの回答を見るときは、一覧の行の「年間ポートフォリオ」を開きます。電話番号などの非公開の連絡先は出ません。',
      office:
        '受講生の登録・招待・修正・在籍の変更の中心画面です。1人ずつ招待する方法と、表計算ファイル（CSV）でまとめて取り込む方法があります。',
    },
    procedures: [
      {
        title: '受講生を探す',
        roles: ['director', 'success', 'office'],
        steps: [
          '左メニュー「会員管理」→「会員一覧」を開く（理事・講師やサクセス担当は「関わった会員」です）',
          '画面上の検索欄に、氏名や会員番号の一部を入れて検索する',
          '在籍の状態（在籍中・卒業など）で絞りたいときは、その見出しをクリックする',
          '見たい人の行の「詳細」をクリックする（行の操作はいつも表示されています。マウスを乗せなくても出ます）',
        ],
      },
      {
        title: '受講生の詳しい情報を見る',
        roles: OFFICE,
        steps: [
          '一覧から対象の人を開く',
          '画面を下にスクロールする。プロフィール、参加したイベント、記録がブロックごとに並んでいます',
          '参加イベントの行をクリックすると、その人のその回の出欠・課題やアンケートの回答が1画面にまとまって出ます',
          '氏名の下の「この会員:」から、プロフィール編集・連携設定編集・参加イベント一覧・社長アンケート・年間ポートフォリオへ移れます',
        ],
      },
      {
        title: '受講生のプロフィールと、これまでの活動を見る',
        roles: ['director', 'success'],
        steps: [
          '左メニュー「関わった会員」を開く（自分が担当した回の対象になった人だけが出ます）',
          '行の「詳細」を押すと、公開プロフィール（顔写真・会社名・自己紹介など）が出ます。ここに提出物や出欠は出ません',
          '提出した課題やアンケート、出欠、採点をまとめて見るときは、同じ行の「年間ポートフォリオ」を押します',
          '年間ポートフォリオでは、4月から翌3月までの記録が1枚に並びます。点数の内訳は出ません（本人と事務局のみ）',
        ],
      },
      {
        title: 'ほかの受講生のプロフィールを見る',
        roles: STUDENT,
        steps: [
          '左メニュー「受講生一覧」を開く',
          '名前をクリックする',
          '顔写真・会社名・自己紹介など、公開と決めた項目だけが見える',
        ],
      },
      {
        title: '受講生を1人招待する',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「会員一覧」を開く',
          '右上の「招待する」をクリックする',
          '氏名、ログインに使うメールアドレス、期、コースを入れる（期とコースは両方必要です）',
          '会社名など、先に分かっている項目があれば入れておく（空でも登録できます）',
          '「招待する」を押すと、送る前の確認画面が出ます。宛先・件名・本文を読んで問題なければ送信する',
          'メールの設定が「実際には送らない（練習）」になっているときは、画面にその旨と、本人に渡す受諾用のリンクが出ます。リンクをコピーして本人に伝えてください',
          '相手が案内のリンクから登録を終えると、この会員にログインがつながります',
        ],
      },
      {
        title: 'CSVで取り込んだ会員を、あとから招待する',
        roles: OFFICE,
        steps: [
          '会員の詳細を開く',
          'ログインがまだつながっていないときは、その案内が出ます',
          'メールアドレスを確認して「招待する」をクリックする',
          '確認画面の内容を見て送信する',
        ],
      },
      {
        title: 'プロフィール（氏名・会社名・顔写真など）を直す',
        roles: OFFICE,
        steps: [
          '会員の詳細を開く',
          '「プロフィールを編集」をクリックする',
          '内容を直して保存する',
          '顔写真は、この編集画面または詳細の写真欄から画像ファイルを選びます（JPEG・PNGなど。大きすぎると送れません）',
        ],
      },
      {
        title: 'Meet用メールや連絡ツールの設定を直す',
        roles: OFFICE,
        steps: [
          '会員の詳細を開く',
          '「連携設定を編集」をクリックする',
          'Google Meetに出るときのメール（1人3件まで）や、Slackなどの宛先を登録する',
          '保存する。宛先の全文は、あとから画面に出ないことがあります（入っているかどうかと末尾だけが見えます）',
        ],
      },
      {
        title: 'パスワードを忘れた受講生を助ける',
        roles: OFFICE,
        steps: [
          '会員の詳細の「ログイン情報」を開く',
          'まず「再設定メールを送る」を使う。本人のメールに、新しいパスワードを決めるリンクが届きます',
          'メールが届かないときだけ「一時パスワードを発行する」。画面に一度だけパスワードが出るので、電話や別の手段で本人に伝える（この画面を閉じると二度と表示されません）',
          '一時パスワードを発行すると、本人が今ログインしている端末はすべてログアウトされます',
          '停止中のアカウントには発行できません。先に在籍の扱いを確認してください',
        ],
      },
      {
        title: '在籍の状態を変える（卒業・退会など）',
        roles: OFFICE,
        steps: [
          '会員の詳細を開く',
          '在籍の変更のボタン（例: 「卒業にする」）をクリックする',
          '確認画面で理由を入れる（記録に残ります）',
          '確定する。退会や除名にすると、その人はログインできなくなります。卒業した人はログインできる運用です',
        ],
      },
      {
        title: '表計算ファイル（CSV）でまとめて登録・更新する',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「会員CSV取込」を開く',
          '画面の説明どおりの列名のファイルを用意する（Excelなら「CSV UTF-8」で保存します）',
          'ファイルを選んでアップロードする',
          '確認の一覧で、新規・更新・エラーの件数を読む。エラー行があるときは理由を直してやり直す',
          '問題がなければ確定する。この時点ではまだログインできません。必要な人は詳細から招待します',
        ],
      },
      {
        title: '会員一覧をCSVで保存する',
        roles: OFFICE,
        steps: [
          '会員一覧の「CSV出力」をクリックする',
          'パソコンにファイルが保存されます。列がずれていないか、開いて1行目を確認してください',
        ],
      },
    ],
    notes: [
      {
        text: '1人の受講生は、期が1つです。別の期でもう一度参加するときは、別の会員として登録します。',
        roles: OFFICE,
      },
      {
        text: '同じメールアドレスで、受講生と理事など複数の立場を同時に持つことはできません。',
        roles: OFFICE,
      },
      {
        text: '会員番号は自動で付きます。手で好きな番号にはできません。',
        roles: OFFICE,
      },
      {
        text: '担任（固定の担当理事）はありません。誰がその受講生の提出物や出欠を見られるかは、イベントの「担当者」で決まります。',
        roles: OFFICE,
      },
      {
        text: '連絡先（電話・連絡用メール）は、ほかの受講生や理事・講師の画面には出ません。本人と事務局（と、ルールで許されたサクセス担当）だけです。',
        roles: ['director', 'success', 'student'],
      },
    ],
  },

  profile: {
    summary: {
      fallback: '自分の顔写真・会社名・連絡先と、ログインの設定を扱う画面です。',
      student:
        '自己紹介・連絡先・顔写真を自分で直す画面です。理事・講師やサクセス担当が指導の参考にします。',
      director:
        '自分の顔写真・会社名・連絡先・自己紹介を登録する画面です。受講生から担当者が分かるようにするための情報です。',
      success:
        '自分の顔写真・会社名・連絡先・自己紹介を登録する画面です。受講生から担当者が分かるようにするための情報です。',
      office:
        '自分の顔写真・会社名・連絡先と、ログイン用の情報を扱う画面です。事務局のログインは認証アプリが基本です。',
    },
    procedures: [
      {
        title: '自己紹介・連絡先を直す',
        roles: STUDENT,
        steps: [
          '左メニュー「マイプロフィール」を開く',
          '自己紹介・電話番号・連絡用メールなどを直す',
          '「保存」をクリックする。保存できました、と出れば完了です',
        ],
      },
      {
        title: '顔写真を登録・変える',
        roles: STUDENT,
        steps: [
          '「マイプロフィール」を開く',
          '「写真を登録」（すでにあれば「写真を変更」）をクリックする',
          'パソコンの中の画像ファイルを選ぶ',
          '選んだ時点で反映されます。「保存」を押す必要はありません',
          '消したいときは「削除」をクリックし、確認画面で実行する',
        ],
      },
      {
        title: '顔写真・会社名・連絡先を登録する',
        roles: ['director', 'success', 'office', 'super_admin'],
        steps: [
          '画面右上の自分の名前をクリックし「アカウント設定」を選ぶ',
          '写真を選び、表示名・会社名・役職・電話・連絡用メール・自己紹介を入れる',
          '「プロフィールを保存する」をクリックする',
        ],
      },
      {
        title: 'ログイン用パスワードを変える',
        roles: ['student', 'director', 'success'],
        steps: [
          '「アカウント設定」を開く',
          '「パスワードの変更」に、今のパスワードを入れる',
          '新しいパスワードを2回入れる（長く、推測されにくいものにしてください）',
          '「パスワードを変更する」をクリックする',
        ],
      },
    ],
    notes: [
      {
        text: '氏名・会社名・役職など、名簿の基本項目は受講生本人では変えられません。事務局へ連絡してください。',
        roles: STUDENT,
      },
      {
        text: 'パスワードを変えると、ほかのパソコンやスマホでログインしたままの状態は切れます。今操作している画面はそのまま使えます。',
        roles: ['student', 'director', 'success'],
      },
      {
        text: 'パスワードを忘れたときは、ログイン画面の「パスワードをお忘れの方」から再設定します。届いたメールのリンクは時間が経つと使えなくなります。届かないときは事務局へ連絡してください。',
        roles: ['student', 'director', 'success'],
      },
      {
        text: '受講生の顔写真・自己紹介は「マイプロフィール」、ログイン関係は右上の「アカウント設定」です。場所が分かれているので、片方を開いて迷っても、もう一方を見てください。',
        roles: STUDENT,
      },
    ],
  },

  portfolio: {
    summary: {
      fallback: '4月から翌3月までの出欠・提出・採点などを1枚にまとめた画面です。',
      student: '自分の1年分の活動をまとめて見る画面です。ほかの受講生の分は開けません。',
      office: '会員詳細の「この会員:」にある「年間ポートフォリオ」から、その受講生の1年分を確認します。',
      director:
        '左メニュー「関わった会員」の一覧で、行の「年間ポートフォリオ」から開きます。会員詳細（プロフィール）からは入れません。点数の内訳は出ません。',
      success:
        '左メニュー「関わった会員」の一覧で、行の「年間ポートフォリオ」から開きます。会員詳細（プロフィール）からは入れません。点数の内訳は出ません。',
    },
    procedures: [
      {
        title: '自分の1年を見る',
        roles: STUDENT,
        steps: [
          '左メニュー「年間ポートフォリオ」を開く',
          '年度が違うときは、画面の年度の選びかたに従って切り替える',
          '出欠、提出した課題やアンケート、採点、社長アンケートを上から読む',
        ],
      },
      {
        title: '受講生の1年を見る',
        roles: OFFICE,
        steps: [
          '会員の詳細を開く',
          '氏名の下の「この会員:」から「年間ポートフォリオ」をクリックする',
          '内容を読む。点数の内訳まで見えるのは事務局（と本人）です',
        ],
      },
      {
        title: '受講生の1年を見る',
        roles: ['director', 'success'],
        steps: [
          '左メニュー「関わった会員」を開く',
          '見たい人の行の「年間ポートフォリオ」をクリックする',
          '出欠・提出した課題やアンケート・採点を上から読む。点数の内訳は出ません',
        ],
      },
    ],
    notes: [
      {
        text: '新しいデータをここに入力する画面ではありません。ほかの画面に入っている記録を、年度で読み直して並べています。',
      },
    ],
  },

  events: {
    summary: {
      fallback: '月例会・分科会・面談など、回ごとの予定と、その回に関する作業をする画面です。',
      student:
        '自分が対象の回だけが一覧に出ます。出欠（出欠を取る回のみ）、課題やアンケート、グループへの連絡は、イベント名をクリックした先のタブで行います。',
      director:
        '自分が担当している回が一覧に出ます。出席者の確認、当日写真、必要なフィードバックは詳細のタブから行います。',
      success:
        '自分が担当している回が一覧に出ます。サクセス面談の記録は、その回の「面談記録」タブに書きます。',
      office:
        '種類ごとのメニュー（月例会、分科会、サクセス面談、1on1 など）から一覧を開き、作成・公開・当日運営まで行います。データはすべて同じ「イベント」です。メニューが違うだけです。種別ごとの「誰が・いつ・画面かリアルか」一覧は、操作マニュアルの「業務フロー表を開く」（事務局のみ）から見られます。',
    },
    procedures: [
      {
        title: '月例会など（たくさんの人が参加する回）を1件作る',
        roles: OFFICE,
        steps: [
          '左メニュー「イベント管理」の下から、作りたい種類の名前（例: 月例会）をクリックする',
          '右上の「月例会の新規登録」のように、種類の名前が入ったボタンをクリックする。種類はここで決まっているので選び直しません（種類を選ばない「イベント一覧」からは新規登録できません）',
          'タイトル、開始と終了の日時、会場、参加の形（会場／オンライン／両方）を入れる',
          '定員は「設定しない」か「人数を指定」を選ぶ。人数を入れるときだけ数を書きます',
          '出欠を取るかどうかを選ぶ（月例会にはこの質問が出ません。月例会は出欠を取らない運用です）。出欠を取るときは、回答の締切日時も必ず入れます',
          'Meetの参加ログを取るかどうかなど、残りの質問に答える',
          '「登録する」をクリックする。この時点では下書きです。受講生には見えません。種類に付いている自動配信ルールから、この回のメール予約が並びます（画面下の「この回のメール」で登録前にも確認できます）',
        ],
      },
      {
        title: '前回の内容をコピーして下書きを作る',
        roles: OFFICE,
        steps: [
          'コピー元のイベント詳細を開く',
          '「この内容で新しい下書きを作る」をクリックし、確認画面で実行する',
          '新しい下書きが開くので、日時などを直す',
          '出欠の回答、課題やアンケートの回答、当日写真、メッセージはコピーされません。対象者の条件と担当者はコピーされます',
        ],
      },
      {
        title: '同じ内容を日付だけ変えて何回も作る',
        roles: OFFICE,
        steps: [
          '左メニュー「イベント管理」→「繰返し作成」を開く',
          '種類、最初の日時、何週間（または何か月）おきか、何回作るかを入れる',
          '登録する。それぞれ別のイベント（下書き）になります。1つずつ中身を確認して公開します',
        ],
      },
      {
        title: '1on1・サクセス面談を表計算ファイルでまとめて作る',
        roles: OFFICE,
        steps: [
          '左メニュー「イベント管理」から「1on1」または「サクセス面談」の一覧を開く',
          '右上の「組み合わせCSVで一括作成」を開く（種別ごとに列が違うため、共通の一括画面は用意していません）',
          '画面のサンプルに沿ったファイルをアップロードし、確認画面でエラーが無いことを見て確定する',
        ],
      },
      {
        title: 'サクセス面談を作る',
        roles: OFFICE,
        steps: [
          '左メニュー「イベント管理」→「サクセス面談」→「新規登録」を開く',
          'タイトルを入れる。日時がまだ決まっていなくても登録できます（画面には「日程調整中」と出ます）',
          '対象の受講生は、先に期を選び、必要ならコースで絞ってから、名前の左の丸印を1人だけ選ぶ',
          '相手はサクセス担当を1人選ぶ（理事・講師は選べません）',
          '登録して公開する。連絡用のグループが自動でできます',
          '日時は、サクセス担当が「実施可能日」に入れた空きから、受講生が1つ選ぶと確定します。確定するとグループに案内が入り、リマインドの予約も作られます',
        ],
      },
      {
        title: '1on1を作る',
        roles: OFFICE,
        steps: [
          '左メニュー「イベント管理」→「1on1」→「新規登録」を開く',
          'サクセス面談と同じく、受講生1人と、相手の理事・講師1人を選ぶ（サクセス担当は選べません）',
          '日時未定でも作れます。候補日は事務局が代理で入れる運用もあります。画面の案内に従ってください',
        ],
      },
      {
        title: '下書きのタイトルや日時を直す',
        roles: OFFICE,
        steps: [
          'イベント詳細の「概要」タブを開く',
          '「編集」をクリックする（青い背景のボタンです。右側の公開・終了のボタンとは別です）',
          '直して保存する。開催日時を変えると、まだ送っていない自動予約の時刻も合わせ直します。この回だけ時刻を変えた通は、合わせ直しの対象外です',
        ],
      },
      {
        title: 'この回の担当者を決める・変える',
        roles: OFFICE,
        steps: [
          '「担当者」タブを開く',
          '月例会などでは、理事・講師とサクセス担当を何人でもチェックできる',
          'サクセス面談ではサクセス担当を1人だけ（丸印）。1on1では理事・講師を1人だけ',
          '「担当者を保存」をクリックする',
          'ここで選んだ人だけが、この回の受講生の出欠や提出を見られます',
        ],
      },
      {
        title: '誰が参加するかを決める',
        roles: OFFICE,
        steps: [
          '「対象者」タブを開く',
          '期とコースなどで条件を付ける。プレビューで名前の一覧を見る',
          'チェックを外して個別に除くこともできる',
          '保存する。公開したあとは名簿が固定されます。あとから会員を増やしても自動では入りません。必要な人は個別追加します',
          '公開後は、名簿の右に「回答・結果」の欄が出ます。この回で使う区分（例: 事前課題）を押すと、その人のその回の回答へ直接開きます',
        ],
      },
      {
        title: '下書きを公開して受講生に見せる',
        roles: OFFICE,
        steps: [
          '右側の「公開する」をクリックする',
          '確認画面でもう一度「公開する」',
          '対象の受講生に見えるようになり、出欠の受付（出欠を取る回の場合）とグループメッセージが始まります',
          'お知らせメールの予約は、イベントを「作ったとき」に入ります。公開が遅れて予約の時刻を過ぎた分は送られません',
        ],
      },
      {
        title: 'この回のメールを確認する・この回だけ直す',
        roles: OFFICE,
        steps: [
          'イベント詳細の「通知」タブを開く（新規登録の画面でも、登録前に同じ一覧の見積もりが出ます）',
          '文面・宛先・配信時刻・状態を確認する。宛先は「参加者」「理事・講師」など日本語で出ます',
          'この回だけ時刻を変えたい通は「時刻を変える」。種類の自動配信ルール自体は変わりません',
          'この回だけ1通足したいときは「この回だけメールを足す」。文面は通知テンプレートから選び、宛先と時刻を指定します',
          '不要な通は「キャンセル」。イベントの通知を全部止めたいときは「通知を停止する」',
          '「プレビュー」で誰に・どんな文面が届くか、「テスト送信」で自分宛に試し送りできます（本番の宛先には行きません）',
        ],
      },
      {
        title: '出欠に答える',
        roles: STUDENT,
        steps: [
          '左メニュー「イベント」から自分の回を開く（対象でない回は出ません）',
          '「出欠」タブを開く（月例会にはこのタブがありません。月例会は出欠を取らず、欠席するときだけ事務局に個別に連絡します）',
          '「現地参加」「オンライン参加」「欠席」など、出ている選択肢を1つクリックする。あとから変えられます',
          '締切の日時が画面に出ます。締切を過ぎると自分では変えられません。直したいときは事務局に連絡してください',
          '欠席申請中は、差し戻されるまで自分では出欠を変えられません',
          '分科会など定員のある回は先着順です。定員に達すると「定員に達しています」と出て、参加は選べません（キャンセル待ちにはなりません）',
        ],
      },
      {
        title: '欠席を申請する',
        roles: STUDENT,
        steps: [
          '「出欠」タブの欠席申請の欄に理由を書く',
          '「欠席申請を送る」をクリックする',
          '事務局が承認すると欠席になります。差し戻されたときは、出欠タブに理由が出ます。必要ならもう一度申請します',
        ],
      },
      {
        title: '欠席申請を承認する・差し戻す',
        roles: OFFICE,
        steps: [
          'ホームの件数、またはイベント詳細の「出欠」タブを開く',
          '申請の内容を読む',
          '承認するか、理由を書いて差し戻す',
        ],
      },
      {
        title: '課題・アンケートの提出状況と提出日を確認する',
        roles: OFFICE,
        steps: [
          'イベント詳細の、課題・アンケートのタブを開く（タブの名前は種類によって違います。月例会は「事前課題」「当日アウトプット」「事後アンケート」「社長ミーティングフィードバック」、分科会は「事前アンケート」「事前課題」「事後アンケート」「アウトプット」です）',
          '割り当てたひな型の下に「提出状況」の表が出ます。窓を開かなくても、そのまま読めます',
          '氏名・状態・提出日時が並びます。まだ一度も開いていない人は「未着手」、下書きだけの人は「未提出（下書き 日時）」と出ます',
          '締切を過ぎてから出した人には「期限超過」が付きます',
          '氏名をクリックすると、その受講生のこの回の結果（出欠・提出物）が開きます。督促はメッセージから送れます',
          '回答の中身は同じタブ内の「個別回答」にそのまま出ます。設問文の確認や集計結果は「設問・集計を見る」を押します',
        ],
      },
      {
        title: '出欠を事務局が直す',
        roles: OFFICE,
        steps: [
          '「出欠」タブの一覧で、直したい人の行の「変更」を押す',
          '開いた窓で、変更後の出欠を選ぶ。押した時点で保存されます',
          '事務局が変更したことは記録に残ります',
          '事務局は締切後でも、定員を超えていても変更できます（手で調整するためです）',
          '手元で席次表や当日の名簿を作るときは、右上の「出欠一覧をCSV出力」から元データを取り出します',
        ],
      },
      {
        title: '誰が来るかを確認する',
        roles: ['director', 'success'],
        steps: [
          '担当している回を開き、「出欠」タブを開く',
          '出席する受講生の名前が並びます。名前をクリックすると、その人のプロフィール（公開項目）が開きます',
          '未回答・欠席・キャンセル待ちの人は名前を出さず、人数だけを最後に出します',
          '出欠を直したり、欠席申請を承認したりはできません。出欠を決めるのは事務局です。直してほしいときは事務局に連絡してください',
        ],
      },
      {
        title: 'Meetの参加ログを取り込んで出欠と突き合わせる',
        roles: OFFICE,
        steps: [
          '「Meet資料」タブを開く（イベントを作るときに「参加ログを取る」を選んだ回だけ出ます。面談の回にはありません）',
          'Google Meetから書き出した参加ログのCSVをアップロードする',
          '会員に登録されたMeet用メールと一字一句同じメールだけが自動で結び付きます',
          '結び付かなかった行は確認の一覧に残るので、会員を選んで確定するか、出欠に使わないかを選ぶ',
          '会員に結び付けると、そのメールがその人のMeet用メールに追加されます（次回から自動で結び付きやすくなります）',
        ],
      },
      {
        title: '当日の席次表を作る',
        roles: OFFICE,
        steps: [
          '席次はこのシステムでは組みません。お手元の表計算ソフトで作ります',
          '出欠を取る回では、イベント詳細の「出欠」タブの右上「出欠一覧をCSV出力」で出席予定者を書き出します',
          '出欠を取らない回（月例会など）は、「対象者」タブの名簿を元にします',
          '書き出したファイルに卓番号を入れて、席次表を仕上げます。できた表は当日の配布や、グループメッセージへの添付で受講生に伝えます',
        ],
      },
      {
        title: '当日の写真を載せる',
        roles: OFFICE,
        steps: [
          '「当日写真」タブを開く（面談にはありません）',
          '画像を選んでアップロードする',
          '対象の受講生も同じタブで見られます',
        ],
      },
      {
        title: 'グループで連絡する',
        steps: [
          '「グループメッセージ」タブを開く',
          '本文を書いて送信する。ファイルを添付することもできます',
          '参加人数と「参加者一覧」から、誰が入っているかを確認できます。受講生の名前をクリックするとプロフィールが開きます',
        ],
      },
      {
        title: '優秀者や態度の入力をする',
        roles: DIRECTOR,
        steps: [
          '担当イベントの詳細を開く',
          '画面の案内「優秀者選定・態度フィードバック」から入力する',
          '選べる人数に上限があります。超えられないときはエラーが出ます',
        ],
      },
      {
        title: 'イベントを終了・アーカイブする',
        roles: OFFICE,
        steps: [
          '右側の「終了にする」「アーカイブにする」をクリックする',
          '確認画面で実行する',
        ],
      },
    ],
    notes: [
      {
        text: 'サクセス面談では、出欠・課題・アンケート・Meet資料・当日写真は使いません。タブも出ませんし、無理に操作しようとしても受け付けません。1on1も同じですが、事後アンケートだけは使えます（タブが1つ出ます）。',
        roles: OFFICE,
      },
      {
        text: '月例会は出欠を取りません。「出欠」タブは出ず、出欠を取るかどうかの質問も作成画面に出ません。欠席の連絡は受講生から事務局への個別連絡で受けます。分科会・EXゼミ・交流会は、回ごとに出欠を取るかどうかを選べます。',
        roles: OFFICE,
      },
      {
        text: '席次はこのシステムでは組みません。出欠一覧のCSV（出欠を取らない回は対象者の名簿）を書き出して、お手元の表計算ソフトで作ります。受講生の画面に卓は出ません。',
        roles: ['office', 'director', 'success'],
      },
      {
        text: '席（卓）はこの画面には出ません。当日の席は、事務局からのご案内をご確認ください。',
        roles: STUDENT,
      },
      {
        text: '公開したあとに対象者を自動では増やしません。追加したい人は対象者タブから個別に足します。',
        roles: OFFICE,
      },
      {
        text: 'イベント同士のつながり（この面談のあと必ずこの1on1、など）はありません。それぞれ別の予定です。',
        roles: OFFICE,
      },
      {
        text: '出欠を直せるのは事務局だけです。理事・講師とサクセス担当は、担当した回の出席者一覧を見るだけです。欠席申請の受付・承認も事務局が行います。',
      },
      {
        text: '分科会は定員を先着順で締めます。定員に達したあとは、受講生は参加を選べません（キャンセル待ちの列にも入りません）。人数を調整したいときは、事務局が「出欠」タブの「変更」で直します。',
        roles: ['office', 'student'],
      },
      {
        text: 'キャンセル待ちは、分科会以外で定員がいっぱいのときに、まだ参加に入れない人が並ぶ列です。欠席が出ると先頭から繰り上がります。欠席申請とは別です。',
        roles: ['office', 'student'],
      },
    ],
  },

  scores: {
    summary: {
      fallback: '月ごとの順位と、自分の点数の内訳を見る画面です。',
      student:
        '公開された月の順位一覧を見ます。点数の内訳は自分の分だけです。1対1の面談の出席は採点に入りません。',
      office:
        '表計算ソフトで計算した採点結果をCSVで取り込み、確認して公開します。計算式そのものはこのシステムの中にはありません。',
    },
    procedures: [
      {
        title: '順位を見る',
        roles: STUDENT,
        steps: [
          '左メニュー「採点結果」を開く',
          '月（期間）を選ぶ',
          '一覧を読む。期とコースも一緒に表示されます',
        ],
      },
      {
        title: '自分の点数の内訳を見る',
        roles: STUDENT,
        steps: ['同じ画面で自分の行を開く', '項目ごとの内訳を読む'],
      },
      {
        title: '採点の材料（生データ）をCSVで書き出す',
        roles: OFFICE,
        steps: [
          '左メニュー「採点」→「採点管理（CSV・公開）」を開く',
          '対象の月を選ぶ',
          '生データの書き出しをクリックする',
          '数字が合わないときは、イベントの出欠・提出・理事の入力が揃っているかを先に確認する',
        ],
      },
      {
        title: '計算結果を取り込んで公開する',
        roles: OFFICE,
        steps: [
          '「採点管理」で、計算済みCSVをアップロードする',
          '件数や順位の重なりなどの確認結果を読む。同じ順位（同率）はエラーにしません',
          '問題なければ「公開する」。公開するまで受講生には見えません',
        ],
      },
      {
        title: '公開を取り消して前の版に戻す',
        roles: OFFICE,
        steps: [
          '対象月の公開済みの版を開く',
          '公開の取り消し（ロールバック）を実行する。直前に公開していた内容に戻ります',
        ],
      },
    ],
    notes: [
      {
        text: '同じ月に同じ版の名前は二度取り込みできません。やり直すときは版の名前を変えます。',
        roles: OFFICE,
      },
      {
        text: 'サクセス面談・1on1の出席は採点の合計に入りません。',
        roles: OFFICE,
      },
    ],
  },

  'office-accounts': {
    summary: {
      fallback:
        '事務局の人がこのシステムに入るためのアカウントです。招待、管理責任者／一般の切替、停止ができます。',
    },
    procedures: [
      {
        title: '事務局の人を招待する',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「事務局アカウント」を開く',
          '右上の「招待する」をクリックする（管理責任者にだけ出ます）',
          'メールアドレスと、管理責任者か一般かを選ぶ',
          '確認画面の文面を見て送信する',
          '相手が受諾するまでは「招待中」と出ます。「取り消す」でやめられます。リンクを再発行することもできます',
        ],
      },
      {
        title: '管理責任者／一般を切り替える',
        roles: OFFICE,
        steps: ['対象の行の権限の切替をクリックする', '確認して実行する'],
      },
      {
        title: 'ログインできないようにする・戻す',
        roles: OFFICE,
        steps: [
          '対象の行の「有効・停止を切替」をクリックする',
          '停止すると、その人はログインできなくなります',
        ],
      },
    ],
    notes: [
      {
        text: '招待できるのは管理責任者だけです。一般の事務局には「招待する」が出ません。',
        roles: OFFICE,
      },
      {
        text: '管理責任者が一人もいなくなる操作はできません。先に別の人を管理責任者にしてください。自分自身を停止・降格することもできません。',
        roles: OFFICE,
      },
      {
        text: '事務局のログインは、Google（または案内された方法）に加えて認証アプリの番号が必要なことがあります。パスワードだけの緊急入口は、決まった場所のネットからしか使えません。',
        roles: OFFICE,
      },
    ],
  },

  staff: {
    summary: {
      fallback:
        '理事・講師とサクセス担当の名簿です。画面は二つに分かれています。招待、プロフィールの代理編集、パスワードを忘れたときの復旧ができます。',
    },
    procedures: [
      {
        title: '理事・講師またはサクセス担当を招待する',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「理事・講師」または「サクセス担当」を開く',
          '右上の「招待する」をクリックする',
          'メールアドレスを入れる。区分（理事かサクセスか）はこの画面で決まっています',
          '分かっているプロフィールがあれば入れておく',
          '確認画面を見て送信する。受諾前は「招待中」から取り消しやリンクの再発行ができます',
        ],
      },
      {
        title: 'プロフィールを代理で直す',
        roles: OFFICE,
        steps: [
          '一覧の「プロフィールを編集」を開く',
          '顔写真・会社名・自己紹介などを直して保存する',
        ],
      },
      {
        title: 'パスワードを忘れた担当者を助ける',
        roles: OFFICE,
        steps: [
          'プロフィール編集または該当の行から、再設定メールを送る（推奨）',
          '届かないときだけ一時パスワードを発行し、画面に一度だけ出た文字列を本人に伝える',
        ],
      },
      {
        title: '退任した人を停止する',
        roles: OFFICE,
        steps: ['一覧の停止の操作をクリックし、確認して実行する', '停止するとログインできなくなります'],
      },
    ],
    notes: [
      {
        text: '受講生の担任として名簿に固定で付ける操作はありません。イベントごとの「担当者」タブで、その回の担当を選びます。',
        roles: OFFICE,
      },
    ],
  },

  masters: {
    summary: {
      fallback:
        'ほかの画面の「選択肢」を先に作る場所です。左メニュー「業務マスター」の下に、期・コース・イベント種別・アンケートテンプレート・席次NG・通知プリセット・通知テンプレートの7つがあります。ここに無いものは、会員登録やイベント作成のプルダウンに出ません。',
    },
    procedures: [
      {
        title: '業務マスターのトップから目的の画面を開く',
        roles: OFFICE,
        steps: [
          '左メニュー「業務マスター」をクリックする（または「マスタトップ」）',
          'カードに「何を登録するか」と「ここでの登録が出てくる場所」が書いてあるので、先に読む',
          '使いたいカードのリンクをクリックして、そのマスターの画面を開く',
        ],
      },
      {
        title: '期（第1期など）を登録する',
        roles: OFFICE,
        steps: [
          '「会員・組織（期）」を開く',
          '画面上の入力欄に、画面に出す名前を入れる（例: 第1期）',
          '登録する。すでに同じ名前（大文字小文字や前後の空白を無視して同じ）があると登録できません',
          '名前を直すときは、一覧の名前の欄をクリックして打ち直す',
          'ここで登録したものが、会員の「期」、会員CSV、イベントの対象者の絞り込みに出ます',
          'もう使わない期は「無効化」します',
        ],
      },
      {
        title: 'コースを登録する',
        roles: OFFICE,
        steps: [
          '「会員・組織（コース）」を開く',
          '画面上の入力欄に、画面に出す名前を入れる',
          '登録する。同じ名前は登録できません',
          '会員は「期」と「コース」の両方必須です。コースが無いと会員を登録できません',
          'ここで登録したものが、会員の「コース」、会員CSV、イベントの対象者の絞り込みに出ます',
          'もう使わないコースは「無効化」します',
        ],
      },
      {
        title: 'イベントの種類を登録する',
        roles: OFFICE,
        steps: [
          '「イベント種別」を開く',
          '名前を入れる（例: 月例会、分科会、サクセス面談）',
          '形式を選ぶ。「通常のイベント」か、「1対1の面談（相手はサクセス担当）」か、「1対1の面談（相手は理事・講師）」',
          '登録する。左メニューの「イベント管理」の下に、この種別名の一覧が自動で増えます',
          '名前や形式を直すときは、行の「編集」を押し、開いた窓で直して保存する',
          'もう使わない種類は「無効化」します。過去のイベントは残ります',
        ],
      },
      {
        title: 'アンケート・課題のひな型を作る',
        roles: OFFICE,
        steps: [
          '「アンケートテンプレート」を開く',
          '新しいひな型を作る。このとき、どのイベント種別で使うか（月例会・分科会など）を必ず選びます',
          '質問を並べる',
          '使えるように「版」を公開する。作っただけではイベントに出ません',
          '実際に配るときは、イベント詳細の課題・アンケートのタブで、このひな型を割り当てる。候補に出るのは、そのイベントの種別に紐づけたひな型だけです',
          '誰が回答を見られるかは、ひな型では選びません（システム側で決まっています）',
        ],
      },
      {
        title: '席次NG（同じ卓にしない組み合わせ）を登録する',
        roles: OFFICE,
        steps: [
          '「席次NG」を開く',
          '上の「会員A」「会員B」を選び、「区分」（例: 取引関係）を入れ、必要なら「理由（機密・事務局のみ閲覧）」を入れて「登録する」を押す',
          '今は席次をこのシステムで組まないため、ここに登録しても警告が出る画面はありません。組み合わせの控えとして残す欄です',
          '席次表はお手元の表計算ソフトで作ります（元データはイベントの「出欠一覧をCSV出力」）',
        ],
      },
      {
        title: '自動配信メールの文面・タイミングを直す',
        roles: OFFICE,
        steps: [
          '「通知テンプレート」（文面）と「通知プリセット」（誰に・いつ）の2画面で設定します',
          'この2つは覚えることが多いので、専用のページにまとめてあります。マニュアル一覧の「自動配信メールの設定（文面とタイミング）」を開いてください',
          'どちらの画面からでも、右上の「❓使い方を見る」で同じページが開きます',
        ],
      },
    ],
    notes: [
      {
        text: '社長アンケート用の「質問マスター」はありません。聞く項目は決まっています。回答の入力は、会員ごとの「社長アンケート」ページか、会員管理の「社長アンケートCSV取込」です。',
        roles: OFFICE,
      },
      {
        text: 'Google・Slack・メール送信の接続は業務マスターではなく、左メニュー「外部連携」です。',
        roles: OFFICE,
      },
      {
        text: '日時未定のサクセス面談では、日時が決まるまで「開催何週間前」のリマインド予約が作れません。日時が決まった時点でまとめて予約されます。1on1は事後アンケートだけ割り当てられます。割り当てて期限を入れると、その期限を起点にした督促が予約されます。',
        roles: OFFICE,
      },
      {
        text: '自分で増やしたイベント種別には、自動配信のプリセットが付いていないことがあります。その場合、イベントを作っても配信予定は増えません。標準の月例会・分科会などは最初から付いています。',
        roles: OFFICE,
      },
    ],
  },

  availability: {
    summary: {
      fallback:
        'サクセス担当が「この日時なら面談できる」を登録する画面です。受講生は、その空きから1つ選んで日時を確定します。',
    },
    procedures: [
      {
        title: '面談できる日時を登録する',
        roles: SUCCESS,
        steps: [
          '左メニュー「実施可能日」を開く',
          '空いている日時を追加する',
          'いらなくなった枠は削除する（すでに選ばれて確定した枠は、画面の案内に従ってください）',
        ],
      },
    ],
    notes: [
      {
        text: '空きは担当者単位で共有されます。ある面談で選ばれると、ほかの面談の候補からは消えます。',
        roles: SUCCESS,
      },
    ],
  },

  'notification-setup': {
    summary: {
      fallback:
        '受講生や理事・講師に自動で届くメールを設定する画面です。「通知テンプレート」（何を書くか）と「通知プリセット」（誰に・いつ送るか）の2つに分かれています。どちらか片方だけでは1通も送られません。2つが組み合わさって、はじめて配信の予定ができます。',
    },
    procedures: [
      {
        title: 'まず仕組みを覚える（この2画面の関係）',
        roles: OFFICE,
        steps: [
          '「通知テンプレート」は、手紙のひな型です。件名と本文だけを持っていて、送る相手も日時も持っていません。ここで文面を作っただけでは、1通も送られません',
          '「通知プリセット」は、イベントの種類ごとの配達指示書です。「月例会用」「分科会用」のように種類ごとに1つあり、その中に「誰に・いつ・どの文面で送るか」というルールが何本も入っています',
          'イベントを作ると、その種類のプリセットに入っているルールの本数だけ、配信の予定が自動で作られます。たとえば月例会のプリセットに5本入っていれば、月例会を1回作るたびに5件の予定ができます',
          '予定ができるのは「イベントを作った時点」です。「公開した時点」ではありません。ただし公開していない回は、時刻が来ても送られずスキップされます',
          '今の標準では、5つの種類（月例会・分科会・EXゼミ・サクセス面談・1on1）に合計26本のルールが入っています。文面のひな型は28件あります（招待メールやパスワード再設定など、プリセットを通さず固定で送られるものも含みます）',
        ],
      },
      {
        title: '文面（通知テンプレート）を直す',
        roles: OFFICE,
        steps: [
          '左メニュー「業務マスター」→「通知テンプレート」を開く',
          '直したい文面の行の「編集」を押す。一覧の下ではなく、画面の中央に別の窓が開きます',
          '「件名」は、受け取った人のメールの一覧に出る1行です。何のメールか分かる言葉を先頭に置きます',
          '「本文」を直す。改行はそのまま反映されます',
          '「送信名義」は、受け取った人に見える差出人の名前です（例: CXO GROWTH 事務局）。空にすると既定の名前になります',
          '保存する。ここで直した文面は、これから送られる分すべてに効きます',
        ],
      },
      {
        title: '文面に、その回の日付や会場を差し込む',
        roles: OFFICE,
        steps: [
          '本文の中に、二重の波かっこで囲んだ合図を書きます。送るときに、その回の実際の値に置き換わります',
          'イベントに連動する通知で使えるのは、イベント名（event_title）・開催日時（starts_at）・会場（venue）の3つです',
          '課題やアンケートに連動する通知では、提出期限（deadline）も使えます',
          '例:「{{event_title}}（{{starts_at}}）を{{venue}}で開催します」と書くと、「9月度月例会（2026/09/15 18:00）をニューオータニで開催します」のように届きます',
          '置き換える値が無いとき（会場を入れていない回など）は、その部分が空になります。エラーにはなりません',
          '書ける合図の一覧は、編集の窓の中に出ています。手で覚える必要はありません',
        ],
      },
      {
        title: '新しい文面を足す',
        roles: OFFICE,
        steps: [
          '「通知テンプレート」の「新規作成」を押す',
          '名前（一覧に出る管理用の名前）・分類・件名・本文・送信名義を入れて保存する',
          'この時点では、まだ1通も送られません。作っただけの状態です',
          '実際に送るには、次の「配信のルールを直す」で、プリセットのルールから この文面を選びます',
        ],
      },
      {
        title: '配信のルール（通知プリセット）を直す',
        roles: OFFICE,
        steps: [
          '左メニュー「業務マスター」→「通知プリセット」を開く。1行が「月例会」「分科会」のように、イベントの種類1つ分です',
          '直したい行の「編集」を押すと、ルールが何本も並んだ編集画面に移ります（別の窓ではありません）。1本＝「1通のメールの予約を作る指示」です',
          '各ルールで決めるのは、宛先・文面・起点・タイミング・時刻の5つです',
          '「宛先」は、参加者／理事・講師／受講生全体／サクセス担当／事務局／全員 から選びます。「参加者」はその回の対象者、「受講生全体」は在籍中の受講生すべてです',
          '「テンプレート」は、通知テンプレートで作った文面から1つ選びます',
          '「起点」は、いつを基準に数えるかです（次の手順で説明します）',
          '「タイミング」は、起点の何日前／何日後かを数字で入れます（例: 1日前）',
          '「配信時刻」は、その日の何時に送るかです（例: 09:00）',
          'ルールを足すときは「ルールを追加」、いらないルールは「削除」を押します',
          '保存する。効くのは、これから作るイベントだけです',
        ],
      },
      {
        title: '「起点」の意味を知る',
        roles: OFFICE,
        steps: [
          '「開催日時」…その回の開始日時を基準にします。いちばんよく使います（例: 1日前の9時＝前日案内）',
          '「終了日時」…その回の終了日時を基準にします',
          '「アンケート公開」「アンケート期限」…課題・アンケートを割り当てた回だけ数えられます。割り当てていない回では予定が作られません（例: 期限の当日朝に未提出者へ督促）',
          '「依頼作成」…依頼を作った日を基準にします',
          '「毎月n日」…イベントとは関係なく、毎月決まった日に送ります。土日にあたったときの前倒し・後ろ倒しも選べます',
          '「毎週◯曜」…イベントとは関係なく、毎週決まった曜日に送ります',
          '最後の2つ（毎月・毎週）は特定の回に紐づきません。配信一覧の「対象イベント」欄には、イベントへのリンクではなく「毎月10日の定期配信」のように周期が出ます',
          '今の標準では、26本のうち18本が開催日時など「イベント起点」、6本が「アンケート起点」、2本が「カレンダー起点（毎月10日・毎週金曜）」です',
        ],
      },
      {
        title: '「送る条件」を付ける',
        roles: OFFICE,
        steps: [
          'ルールには、送る条件を1つ付けられます。条件を満たさない回は、時刻が来ても送らずスキップされます',
          '「未提出者がいる場合のみ送る」…督促メールに使います。全員が出し終わっていれば送りません',
          '「今週分の議事録がある場合のみ送る」…毎週金曜の事務局向けの確認メールに使います',
          '条件を空にすると、必ず送ります',
        ],
      },
      {
        title: '送る前に、文面と宛先を確かめる',
        roles: OFFICE,
        steps: [
          '左メニュー「システム通知（配信予定・履歴）」を開く',
          '確かめたい行の「文面を見る」を押すと、実際に送られる件名・本文と、届く相手の一覧が出ます。差し込みも反映された状態で見られます',
          '「自分宛にテスト送信」を押すと、自分にだけ1通届きます。受講生には届きません',
          'この回はやめたいときは「この配信を止める」を押します',
        ],
      },
      {
        title: 'この回だけ、時刻を変える・1通足す',
        roles: OFFICE,
        steps: [
          'イベント詳細の「通知」タブを開く（プリセットではありません）',
          '時刻を直したい行で日時を変えると、その回だけ変わります。プリセット本体は変わりません',
          '「1通追加」で、この回にだけ送るメールを足せます',
          'ここで手で直した時刻は、あとから開催日時を変えても自動計算に戻りません（開催日時を変えるときに確認が出ます）',
        ],
      },
    ],
    notes: [
      {
        text: 'プリセットを直しても、すでに作ってあるイベントの配信予定は作り直されません。直したルールが効くのは、これから作る回だけです。すでに作った回を直すときは、イベント詳細の「通知」タブを使います。',
        roles: OFFICE,
      },
      {
        text: '文面を「無効化」しても、すでに入っている配信予定は消えません。送らないようにするには、配信予定側で1件ずつ止めるか、プリセットからそのルールを削除します。',
        roles: OFFICE,
      },
      {
        text: '自分で増やしたイベント種別には、自動配信のプリセットが付いていません。その種別でイベントを作っても配信予定は1件も増えません。標準の月例会・分科会・EXゼミ・サクセス面談・1on1には最初から付いています。',
        roles: OFFICE,
      },
      {
        text: '課題・アンケートの期限を起点にしたルールは、その回に課題・アンケートを割り当てて期限を入れて はじめて予定が作られます。割り当てていない回では、ルールがあっても予定は増えません。',
        roles: OFFICE,
      },
      {
        text: '日時未定でサクセス面談・1on1を作った場合、「開催◯日前」のルールは日時が決まるまで予定を作れません。受講生が候補日を選んで日時が確定した時点で、まとめて予定が入ります。',
        roles: OFFICE,
      },
      {
        text: 'メールの送信サーバー（SMTP）の設定は、業務マスターではなく左メニュー「外部連携」です。送信を止めている間も配信予定は同じように作られ、あとから繋いだときにその予定行がそのまま使われます。',
        roles: OFFICE,
      },
      {
        text: '招待メールとパスワード再設定メールは、プリセットを通さず、その場で1通だけ送るものです。文面は通知テンプレートで直せますが、配信予定の一覧には出ません。',
        roles: OFFICE,
      },
      {
        text: '文面の一覧には、どのルールからも使われていないものが混ざっています。今は「席次・プロフィールの案内」がそれで、席次をシステムで扱うのをやめたときにルールを外したため、残っていても送られません。使いたいときは、プリセットにルールを足してこの文面を選びます。',
        roles: OFFICE,
      },
    ],
  },

  notifications: {
    summary: {
      fallback: 'システムが自動で送るメールなどのお知らせです。人とのやり取り（返信）ではありません。',
      student: '自分宛てのお知らせの一覧です。件名を開いて本文を読み、案内の場所へ進みます。',
      office:
        '1件の回ならイベント詳細の「通知」タブ、全体なら「システム通知（配信予定・履歴）」で予約と履歴を見ます。自分宛ての受信箱ではありません。自分宛てを見るのは画面右上のベルです。',
    },
    procedures: [
      {
        title: '届いたお知らせを読む',
        roles: STUDENT,
        steps: [
          '右上のベル、または左メニュー「システム通知」を開く',
          '件名をクリックする',
          '本文のリンクから、イベントやメッセージなど必要な画面へ進む',
        ],
      },
      {
        title: '配信の予約を確認する・止める',
        roles: OFFICE,
        steps: [
          '左メニュー「システム通知（配信予定・履歴）」を開く。イベントをまたいだ一覧です。1件の回だけ見るときは、イベント詳細の「通知」タブが分かりやすいです',
          '「状態」を切り替えて、これから送るものと、送ったもの・スキップしたものを確認する',
          '「対象イベント」の欄が「イベントを開く」なら、その回に紐づく配信です。クリックするとイベントが開きます',
          '「毎月10日の定期配信」「毎週金曜の定期配信」と出ているものは、特定の回ではなく決まった日・曜日に送るものです。イベントとは無関係に届きます',
          '「文面を見る」で実際に送られる件名・本文と宛先を確認できます。「自分宛にテスト送信」なら自分にだけ届きます',
          '不要な予約は「この配信を止める」でキャンセルする',
          '公開前のイベントは、時刻になっても送らずスキップされます',
        ],
      },
      {
        title: '全員やロール向けに、1回限りのお知らせを送る',
        roles: OFFICE,
        steps: [
          '手入力の配信を新規で開く',
          '宛先（全員／受講生全員／理事・講師／サクセス担当）を選ぶ',
          '件名と本文を入れる',
          'すぐ送るか、日時を指定する。過去の日時は受け付けません',
        ],
      },
    ],
    notes: [
      {
        text: 'お知らせの予約はイベントを作ったときに入ります。「公開した瞬間に予約される」ではありません。SMTPサーバーを後から繋いでも、同じ予約行が実送信されます。',
        roles: OFFICE,
      },
      {
        text: '毎月10日のまとめ配信と、毎週金曜の議事録確認の2件は、イベントとは無関係に決まった日・曜日に届きます。「対象イベント」の欄にイベントへのリンクではなく周期が出ているものがそれです。止めたいときは、通知プリセットからそのルールを外します。',
        roles: OFFICE,
      },
      {
        text: '文面やタイミングを変えたいときは、業務マスターの「通知テンプレート」「通知プリセット」です。詳しくはマニュアルの「自動配信メールの設定（文面とタイミング）」を読んでください。',
        roles: OFFICE,
      },
    ],
  },

  president: {
    summary: {
      fallback:
        '社長への聞き取り結果を残す画面です。受講生がフォームで答えるものではありません。内容は機微な情報のため、これから登録するものは事務局だけが見られます。',
      student:
        '自分に開示された「社長からの期待」を読む画面です。今の運用では、社長への聞き取り結果は事務局だけが見る扱いのため、ここには何も出ないことがあります。',
      director:
        '今の運用では、社長への聞き取り結果は事務局だけが見る扱いです。以前の運用で開示された分だけが会員詳細に出ます。',
      office:
        '6つの決まった項目を、会員ごとの「社長アンケート」ページで入力するか、1人1行のCSVで取り込みます。質問のマスターはありません。登録したものは事務局だけが見られます。',
    },
    procedures: [
      {
        title: '1人分を画面から入力する',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「会員一覧」から、対象の受講生の行の「詳細」を押す',
          '氏名の下に並ぶ「この会員:」の中から「社長アンケート」を押す（プロフィール編集や参加イベント一覧と同じ並びです）',
          '「+ ヒアリング結果を代理入力」を押すと、入力欄が開きます',
          '種別を選ぶ。「START入口」は現在の評価（As is）・期待する状態（To be）・特に伸ばしてほしい領域の3項目、「成長評価」は成長したと感じる点・満足度（5段階）・課題と感じる点の3項目です',
          '実施回（例: 3期 期中）・聴取日・聴取者（事務局の担当者名）を入れる',
          '開示範囲を選ぶ欄はありません。登録したものは事務局だけが見られます（機微な情報のため、システム側で固定しています）',
          '項目を書いて「登録する」を押す。登録したものは同じページの一覧に並び、押すと中身の確認・修正・削除ができます',
          '会員詳細（「会員詳細」タブ）にも同じ内容が出ますが、そちらは読むだけです。入力・修正はこのページで行います',
        ],
      },
      {
        title: 'CSVでまとめて取り込む',
        roles: OFFICE,
        steps: [
          '左メニュー「会員管理」→「社長アンケートCSV取込」を開く',
          '1人1行のファイルをアップロードし、確認して確定する',
          'ファイルに開示範囲の列があっても使いません。取り込んだものは事務局だけが見られます（画面に注意が出ます）',
        ],
      },
      {
        title: '自分に開示された内容を読む',
        roles: STUDENT,
        steps: [
          '左メニュー「社長からの期待」を開く',
          '表示されている文章を読む',
          '何も出ないときは、開示されている内容が無いということです（今の運用では事務局だけが見ます）',
        ],
      },
      {
        title: '担当する受講生の分を読む',
        roles: DIRECTOR,
        steps: [
          '会員の詳細を開く',
          '社長アンケートの欄を読む（開示されているものだけ。読むだけで、書き換えはできません）',
          '今の運用で新しく登録されたものは事務局だけが見るため、ここには出ません',
        ],
      },
    ],
    notes: [
      {
        text: '社長用のログインや、質問を自由に増やす画面はありません。',
        roles: OFFICE,
      },
      {
        text: '聞き取りの内容は機微な情報のため、これから登録するものは事務局だけが見られます。誰に見せるかを選ぶ欄はありません。以前の運用で本人・理事へ開示した分は、そのまま見えます。',
        roles: OFFICE,
      },
    ],
  },

  surveys: {
    summary: {
      fallback:
        '課題やアンケートは、イベントの詳細にある4つのタブです。独立した「課題一覧」メニューはありません。タブの名前はイベントの種類によって変わります。',
      student:
        'ホームの「次にやること」、通知、またはイベント詳細のタブから開いて答え、最後に「提出する」を押します。回答欄はタブの中にそのまま開きます。',
      office:
        'ひな型は業務マスターで作り、イベントのタブで割り当てます。提出状況と回答は、そのタブの中にそのまま並びます。',
    },
    procedures: [
      {
        title: 'ひな型を作ってイベントに割り当てる',
        roles: OFFICE,
        steps: [
          '左メニュー「業務マスター」→「アンケートテンプレート」で、使うイベント種別を選んで質問を作り、版を使えるようにする',
          'イベント詳細で、該当のタブを開く',
          'ひな型を選び、締切を入れて割り当てる。候補に出るのは、そのイベントの種別に紐づけたひな型だけです',
          'この回では使わない種類は、イベント作成時やタブ上の「使わない」で明示できます。その場合、ホームの「次にやること」では済んだ扱いになります',
          'イベントを公開する',
        ],
      },
      {
        title: 'タブの名前を確かめる（種類ごとに違います）',
        roles: OFFICE,
        steps: [
          '月例会は「事前課題」「当日アウトプット」「事後アンケート」「社長ミーティングフィードバック」の4つです（事前アンケートはありません）',
          '分科会は「事前アンケート」「事前課題」「事後アンケート」「アウトプット」の4つです',
          'EXゼミ・交流会など、そのほかの種類は「事前アンケート」「事前課題」「事後アンケート」「事後課題」の4つです',
          'サクセス面談にはタブが出ません。1on1は「事後アンケート」だけ出ます',
        ],
      },
      {
        title: '提出状況と回答を見る',
        roles: OFFICE,
        steps: [
          '同じタブで、提出状況（氏名・状態・提出日時）を上から読む',
          'その下の「個別回答」に、回答の中身がそのまま並びます。窓を開く必要はありません',
          '設問文の確認や集計結果を見たいときは「設問・集計を見る」を押します（別の窓で出ます）',
          '回答が0件でも、設問のプレビューは見られます',
          '氏名をクリックすると、その受講生のこの回の結果（出欠・提出物）へ移ります',
        ],
      },
      {
        title: '課題・アンケートに答える',
        roles: STUDENT,
        steps: [
          'ホーム、通知、またはイベント詳細の該当タブから開く',
          '「ここから回答する」を押すと、同じタブの中に回答欄が開きます（別の窓は出ません）',
          '質問は1問ずつ枠に分かれています。必須の印があるものは空にできません',
          '途中でやめるときは「閉じる」。途中保存ができる場合は画面のボタンに従う',
          '「提出する」をクリックする。締切までは出し直せる場合があります',
        ],
      },
    ],
    notes: [
      {
        text: 'インターネット上の誰でも開ける回答用URLはありません。ログインした受講生だけが答えます。',
      },
      {
        text: 'サクセス面談には課題もアンケートも割り当てられません。1on1は事後アンケートだけ割り当てられます（事前アンケート・事前課題・事後課題は使えません）。',
        roles: OFFICE,
      },
      {
        text: '月例会は「事前課題 → 当日アウトプット → 事後アンケート → 社長ミーティングフィードバック」の順に使います。タブもこの順に並んでいます。',
        roles: OFFICE,
      },
    ],
  },

  messages: {
    summary: {
      fallback:
        '人と人がやり取りする画面です。種類は「事務局との1対1」と「イベントのグループ」の2つだけです。件名はありません。全員へのお知らせはシステム通知です。',
      student: '事務局への質問と、自分が入っているイベントのグループが使えます。',
      director: '担当している回のグループと、事務局とのやり取りが使えます。',
      success: '担当している回のグループと、事務局とのやり取りが使えます。',
      office:
        '「問い合わせ対応」で届いた会話に返信し、「連絡先」から相手を選んで話を始めます。同じ相手とは1本の会話にまとまります。',
    },
    procedures: [
      {
        title: 'メッセージを書いて送る（共通の操作）',
        steps: [
          '会話を開くと、下に入力欄があります。そこに本文を書きます',
          'Enterキーを押すと送信されます。改行したいときは Shift キーを押しながら Enter を押します',
          '日本語の変換中に Enter を押しても送信されません。変換の確定にだけ使われます。変換が終わったあと、もう一度 Enter を押すと送信されます',
          '「送信」ボタンを押しても同じです',
          'ファイルを付けるときは、入力欄の下にあるクリップの絵（📎）を押してファイルを選びます。会話の画面にファイルをそのまま放り込んでも付きます',
          '本文が空でも、ファイルだけで送れます',
        ],
      },
      {
        title: '会話の画面の見かた',
        steps: [
          '自分の発言も相手の発言も、同じ向き（左揃え）で上から順に並びます。左右に分かれた吹き出しではありません',
          '発言の左に顔写真、右に名前と時刻が出ます',
          '同じ人が続けて書いたときは、2件目からは顔写真と名前が省かれて本文だけが続きます。その行にマウスを乗せると、左に時刻が出ます',
          '日付が変わるところに横線が入り、真ん中に「今日」「昨日」「2026年9月1日（火）」のように出ます',
          '自分が書いたものを消したいときは、その行にマウスを乗せます。右上に「削除」が出ます',
          '相手が読むと、その人の顔写真が「既読」として本文の下に出ます',
        ],
      },
      {
        title: '事務局に質問する',
        roles: ['student', 'director', 'success'],
        steps: [
          '左メニュー「メッセージ」を開く',
          '新規、または事務局との会話を開く',
          '本文を書いて送信する',
        ],
      },
      {
        title: 'イベントのグループで、特定の人に呼びかける',
        steps: [
          'イベント詳細の「グループメッセージ」タブ、またはメッセージ一覧のグループを開く',
          '入力欄で @ を打つと、そのグループにいる人の名前が下に出ます',
          '名前をクリックするか、Enterキーで先頭の候補を選ぶと、本文に差し込まれます',
          '送信すると、呼ばれた人には「あなた宛の呼びかけ」という件名でお知らせが届きます。呼んでいない人にも、いつもどおり新着のお知らせは届きます',
          '呼ばれた本人の画面では、その名前が黄色く光り、その行の左に線が付きます。自分以外が呼ばれたところは青いままです',
          '候補に出ない人（そのグループにいない人）の名前を書いても、その人には届きません',
        ],
      },
      {
        title: 'メッセージにリアクションで返す',
        steps: [
          'どのメッセージにも、本文の下に「☺+」の小さなボタンが出ています',
          '押すと絵文字（👍🙏✅😊🎉👀）が並ぶので、返したいものを1つ選びます',
          '選ぶと、そのメッセージの下に絵文字と人数が並びます',
          'もう一度同じ絵文字を押すと、自分の分だけ取り消せます',
          '絵文字の上にマウスを乗せると、押した人の名前が出ます',
          '「読みました」「了解しました」だけを返したいときに使うと、返信を書かずに済みます',
        ],
      },
      {
        title: '問い合わせに返信する',
        roles: OFFICE,
        steps: [
          '左メニュー「メッセージ」→「問い合わせ対応」を開く',
          '行をクリックして会話を開く',
          '本文を書いて送信する',
        ],
      },
      {
        title: 'こちらから話しかける',
        roles: OFFICE,
        steps: [
          '「連絡先」を開く',
          '相手の名前をクリックする（まだやり取りが無くても一覧に出ます）',
          '本文を書いて送信する',
        ],
      },
    ],
    notes: [
      {
        text: '事務局が会話の中に書く内部メモは、受講生・理事・サクセス担当には見えません。',
        roles: OFFICE,
      },
      {
        text: 'メッセージの赤丸は未読です。返信しただけでは消えず、開いて読むと消えます。',
      },
      {
        text: '会話の見た目は Slack や LINE と同じ考え方です。日付の区切り、同じ人の続けての発言のまとめ、マウスを乗せたときだけ出る操作、という順で読んでください。',
      },
      {
        text: '事務局への1対1の問い合わせでは、@ で事務局を呼ぶことはできません（候補にも出ません）。2人だけのやり取りなので、書けば必ず相手に届きます。@ が役に立つのはイベントのグループです。',
      },
      {
        text: 'リアクションは6種類だけです。押せる絵文字を増やしたいときは事務局にご相談ください（システムの設定で増やします）。',
      },
    ],
  },

  inbox: {
    summary: {
      fallback:
        '受講生などから事務局への問い合わせが並ぶ箱です。担当者・優先度・対応状態の欄は使いません。差出人と、最後に動いた日時が分かります。',
    },
    procedures: [
      {
        title: '問い合わせを読んで返信する',
        roles: OFFICE,
        steps: [
          '左メニュー「メッセージ」→「問い合わせ対応」を開く',
          '行をクリックして会話を開く',
          '必要なら会員の名前からプロフィールを開く',
          '返信を書く。内部メモが必要なときは、相手に見えないメモ欄を使う',
        ],
      },
    ],
    notes: [
      {
        text: '赤い丸は未読の会話です。「まだ対応していない件数」ではありません。開くと減ります。',
        roles: OFFICE,
      },
    ],
  },

  success: {
    summary: {
      fallback: 'サクセス面談の進め方と、面談記録の残し方の説明です。',
      success:
        '空き日時の登録、担当した面談イベントの確認、面談記録の記入までを行います。',
      office: '面談イベントの作成と、記録の確認ができます。',
    },
    procedures: [
      {
        title: '面談までと記録の残し方',
        roles: SUCCESS,
        steps: [
          '左メニュー「実施可能日」に空きを入れる',
          '「イベント一覧」からサクセス面談を開く',
          '日時が未定なら、受講生が候補から選ぶまで待つ',
          '当日の連絡は「グループメッセージ」',
          '終わったら「面談記録」タブに書き、公開する相手を選んで保存する',
        ],
      },
      {
        title: '事務局が記録を確認する',
        roles: OFFICE,
        steps: [
          '該当のサクセス面談イベントを開く',
          '「面談記録」タブを読む。事務局は公開範囲に関係なく確認できます',
        ],
      },
    ],
    notes: [
      {
        text: '1on1の記録も、その1on1イベントのタブ側で扱います。年に1本だけ続く専用の会話ではありません。',
      },
    ],
  },

  integrations: {
    summary: {
      fallback:
        'メール送信や、Slackなどの連絡ツールをつなぐ設定です。パスワード類は保存後に画面へ再表示しません。',
    },
    procedures: [
      {
        title: '接続情報を登録する',
        roles: OFFICE_AND_SUPER,
        steps: [
          '左メニュー「外部連携」を開く',
          '「接続設定」で、使う項目だけ入力して保存する',
          '未入力の項目は、サーバー側に用意した値があればそれを使います',
          '受講生ごとの宛先は、会員詳細の「連携設定を編集」で登録する',
        ],
      },
    ],
    notes: [
      {
        text: '写真や提出ファイルは、別会社のネット上の倉庫ではなく、このサーバーのディスクに保存されます。',
        roles: OFFICE,
      },
      {
        text: 'Chatworkを使うかどうか、LINEを使うかどうかは、運用の方針確認の対象です。画面に項目があっても、使わないと決めた手段は無理に埋めないでください。',
        roles: OFFICE,
      },
    ],
  },

  'audit-logs': {
    summary: {
      fallback: '誰が・いつ・何をしたかの記録です。消したり書き換えたりはできません。',
    },
    procedures: [
      {
        title: '操作の記録を調べる',
        roles: OFFICE_AND_SUPER,
        steps: [
          '左メニュー「監査ログ」を開く',
          '操作の種類や日付で絞り込む',
          '行をクリックすると、別ウィンドウで詳しい内容が出ます',
        ],
      },
    ],
    notes: [
      {
        text: '画面の操作名は日本語で出ます。分からない英数字だけの名前が出たときは、その文字を控えてシステム運営へ聞いてください。',
        roles: OFFICE,
      },
    ],
  },

  superadmin: {
    summary: {
      fallback:
        'このシステムを預かる会社専用の画面です。事務局の緊急復旧、ジョブの失敗の確認、保守の切替などができます。',
    },
    procedures: [
      {
        title: '障害やジョブ失敗を確認する',
        roles: SUPER_ADMIN,
        steps: [
          '「ジョブ・障害対応」を開く',
          '失敗している処理を読む',
          '必要なら再実行する。操作は監査ログに残ります',
        ],
      },
      {
        title: '事務局が入れなくなったときの緊急対応',
        roles: SUPER_ADMIN,
        steps: [
          '「事務局管理者」から対象を確認する',
          '案内された手順で復旧する（認証のリセットなど）',
          '本番で「どのネットからでもパスワードログイン可」にはしない',
        ],
      },
    ],
    notes: [
      {
        text: '会員の日常業務（イベント作成など）は事務局の画面で行います。こちらは緊急・保守用です。',
        roles: SUPER_ADMIN,
      },
    ],
  },
};
